import { ArrowLeft, ExternalLink, Zap, Brain, Users, BarChart, Search, Code, Layout, Bot, Palette, Video, FileText, Globe, Monitor, Server, Database, Cloud, AlertTriangle, Fish, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from 'react-i18next';

// ---------------------------------------------------------------------------
// カテゴリ定義（日本語）
// ---------------------------------------------------------------------------
const categoriesJa = [
  { key: "三大AIプラットフォーム", label: "三大AIプラットフォーム", subtitle: "あらゆるビジネスの土台となる万能AI。まず押さえるべき3つ" },
  { key: "Claudeエコシステム", label: "Claude エコシステム", subtitle: "Anthropicが提供するClaude Code・Fable・APIモデル群。AI駆動開発の中核" },
  { key: "AI検索・リサーチ", label: "AI検索・リサーチ", subtitle: "調べものを圧倒的に速く。根拠付きで信頼できる情報収集" },
  { key: "AIコーディング", label: "AIコーディング・開発", subtitle: "エンジニアの生産性を数倍に引き上げるAI開発ツール" },
  { key: "ビルドエージェント", label: "ビルドエージェント", subtitle: "コードを書かずにWebアプリを作る。非エンジニアでもプロダクト開発" },
  { key: "業務自動化", label: "AIエージェント・業務自動化", subtitle: "繰り返し業務をAIに任せる。人手ゼロで回るワークフロー構築" },
  { key: "画像・デザイン", label: "画像・デザイン生成", subtitle: "デザイナー不在でもプロ品質。マーケ素材からブランディングまで" },
  { key: "動画・音声", label: "動画・音声生成", subtitle: "撮影・収録なしで動画・ナレーションを制作。多言語展開も一瞬" },
  { key: "ドキュメント・効率化", label: "ドキュメント・ビジネス効率化", subtitle: "資料作成・翻訳・議事録。日々の書類仕事をAIが肩代わり" },
  { key: "バックエンド・インフラ", label: "開発基盤・インフラ", subtitle: "GitHub / Vercel / Cloudflare / Supabase — 現代のWeb開発を支える4大プラットフォーム" },
  { key: "日本のAI", label: "日本発AI", subtitle: "国産・日本語特化AIの最前線。製造業・サービス業での活用実績" },
  { key: "中国系AI", label: "中国系AI ⚠️ 利用注意", subtitle: "コスト効率・性能で注目されるが、データ取り扱いに十分な注意が必要" },
];

// ---------------------------------------------------------------------------
// カテゴリ定義（英語）
// ---------------------------------------------------------------------------
const categoriesEn = [
  { key: "Big3AIplatforms", label: "Big 3 AI Platforms", subtitle: "Versatile AI that forms the foundation for any business. The 3 essentials to know first" },
  { key: "ClaudeEcosystem", label: "Claude Ecosystem", subtitle: "Claude Code, Fable, and API model suite from Anthropic. The core of AI-driven development" },
  { key: "AISearchResearch", label: "AI Search & Research", subtitle: "Research dramatically faster. Reliable information gathering with cited sources" },
  { key: "AICoding", label: "AI Coding & Development", subtitle: "AI development tools that multiply engineer productivity" },
  { key: "BuildAgents", label: "Build Agents", subtitle: "Create web apps without coding. Product development even for non-engineers" },
  { key: "WorkflowAutomation", label: "AI Agents & Workflow Automation", subtitle: "Let AI handle repetitive tasks. Build workflows that run without human intervention" },
  { key: "ImageDesign", label: "Image & Design Generation", subtitle: "Professional quality without a designer. From marketing materials to branding" },
  { key: "VideoAudio", label: "Video & Audio Generation", subtitle: "Create videos and narrations without filming. Multilingual rollout in an instant" },
  { key: "DocEfficiency", label: "Document & Business Efficiency", subtitle: "AI handles document creation, translation, and meeting minutes. Daily paperwork, automated" },
  { key: "BackendInfra", label: "Development Platform & Infrastructure", subtitle: "GitHub / Vercel / Cloudflare / Supabase — the 4 major platforms supporting modern web development" },
  { key: "JapanAI", label: "Japan-Born AI", subtitle: "Japan's domestic and Japanese-optimized AI at the forefront. Application track records in manufacturing and service industries" },
  { key: "ChinaAI", label: "Chinese AI ⚠️ Use with Caution", subtitle: "Attracting attention for cost efficiency and performance, but data handling requires careful attention" },
];

// ---------------------------------------------------------------------------
// ツール定義（日本語）
// ---------------------------------------------------------------------------
const aiToolsJa: {
  id: number;
  name: string;
  category: string;
  description: string;
  purpose: string;
  features: string[];
  pricing: string;
  url: string;
  icon: React.ReactNode;
  isChina?: boolean;
}[] = [
  // ── カテゴリ1: 三大AIプラットフォーム ──────────────────────────
  {
    id: 1,
    name: "ChatGPT",
    category: "三大AIプラットフォーム",
    description: "「何でも相談できる万能アシスタント」。文章作成、画像生成、音声会話、Web検索、データ分析——ひとつの画面であらゆる仕事をこなせます。GPTsで自分専用のAIを作れるのも強み。",
    purpose: "「まず何から始めればいいか分からない」人の最初の一歩に最適。日報・メール・企画書の下書きから画像生成まで、1ツールで完結できます。",
    features: ["マルチモーダル対応", "画像生成(GPT Image)", "音声対話", "GPTs/カスタムAI", "Web検索統合"],
    pricing: "無料 / Go $8/月 / Plus $20/月 / Pro $200/月",
    url: "https://chat.openai.com",
    icon: <Brain className="h-6 w-6" />,
  },
  {
    id: 2,
    name: "Gemini",
    category: "三大AIプラットフォーム",
    description: "「Google製品をAIで丸ごと強化する」。Gmail・Docs・Sheets・Driveと直結し、200万トークンの超長文を一括処理。Deep Research機能で専門レポートも自動生成します。",
    purpose: "Google Workspaceを日常的に使う企業なら導入効果が絶大。メールの要約、スプレッドシートの分析、大量PDFの横断検索がワンクリックに。",
    features: ["200万トークン処理", "Google Workspace統合", "Deep Research", "100言語対応", "動画理解"],
    pricing: "無料 / AI Pro $19.99/月(2TB付) / AI Ultra 要問合せ",
    url: "https://gemini.google.com",
    icon: <Brain className="h-6 w-6" />,
  },
  {
    id: 3,
    name: "Claude",
    category: "三大AIプラットフォーム",
    description: "「長文と正確さに強い、慎重派のAI」。20万トークンの長文を正確に処理し、Artifactsでコードやドキュメントをその場で生成。Computer Useでパソコン操作まで自動化します。",
    purpose: "契約書・法律文書・技術仕様書など「正確さが命」の業務に。Projectsで社内資料をまとめて読み込ませれば、専属アシスタントのように動きます。",
    features: ["20万トークン処理", "Projects(専用知識庫)", "Artifacts", "Computer Use", "高い正確性"],
    pricing: "無料 / Pro $20/月 / Max $100〜200/月 / Team $25〜30/席/月",
    url: "https://claude.ai",
    icon: <Zap className="h-6 w-6" />,
  },

  // ── カテゴリ2: Claudeエコシステム ──────────────────────────────
  {
    id: 101,
    name: "Claude Code",
    category: "Claudeエコシステム",
    description: "「ターミナルに住むAIエンジニア」。100万トークンの超大規模コンテキストでプロジェクト全体を理解し、複数ファイルの同時編集・Git操作・テスト実行までワンストップ。サブエージェントによる並列作業やMCPサーバー連携で拡張性も抜群。",
    purpose: "エンジニアの「コードを書く→テスト→コミット」の一連の流れを大幅に加速。大規模リファクタリングやレビューを数分で完了させます。Hooksで独自の自動化も構築可能。",
    features: ["100万トークンコンテキスト", "サブエージェント並列実行", "Git操作自動化", "MCP拡張", "Hooks対応", "構造化出力"],
    pricing: "Pro $20/月 / Max $100〜200/月 / Team Premium $150/席/月",
    url: "https://docs.anthropic.com/en/docs/claude-code",
    icon: <Code className="h-6 w-6" />,
  },
  {
    id: 102,
    name: "Claude Fable (claude-fable-5)",
    category: "Claudeエコシステム",
    description: "「Anthropicの最新フラッグシップモデル」。複雑な推論・長期タスク・高度なコーディングに特化した次世代モデル。Claude Codeのデフォルトとしても利用可能で、AI駆動開発のベースに最適です。",
    purpose: "高精度が要求される設計レビュー・アーキテクチャ決定・複雑なバグ解析に。従来モデルより深い推論能力でビジネス上の難問も解決します。",
    features: ["高度推論", "長期タスク対応", "コーディング特化", "Claude Code統合", "API利用可"],
    pricing: "Claude API従量課金 / Claude Maxプラン内で利用可",
    url: "https://www.anthropic.com",
    icon: <Sparkles className="h-6 w-6" />,
  },
  {
    id: 103,
    name: "Claude APIモデル群 (Opus / Sonnet / Haiku)",
    category: "Claudeエコシステム",
    description: "「用途とコストで使い分けるClaude 4ファミリー」。Opus 4.8（最高品質・複雑タスク）、Sonnet 4.6（バランス型・汎用）、Haiku 4.5（高速・低コスト・大量処理）の3ティアで最適化できます。",
    purpose: "自社アプリへのAI機能組み込みに。チャットボット・文書要約・分類・コード生成など用途別に最適モデルを選択し、品質とコストのバランスを取れます。",
    features: ["Opus 4.8(最高品質)", "Sonnet 4.6(汎用バランス)", "Haiku 4.5(高速・低コスト)", "Prompt Caching", "Tool Use / MCP対応"],
    pricing: "Opus 4.8: $15/$75/MTok / Sonnet 4.6: $3/$15/MTok / Haiku 4.5: $0.8/$4/MTok",
    url: "https://www.anthropic.com/api",
    icon: <Zap className="h-6 w-6" />,
  },
  {
    id: 104,
    name: "OpenAI Codex CLI",
    category: "Claudeエコシステム",
    description: "「ターミナルからもChatGPTからも使えるAIコーディングエージェント」。オープンソースCLIとChatGPT統合の二刀流。GPT-4oをデフォルトモデルに、スクリーンショット入力や長時間自律実行にも対応。",
    purpose: "「このバグを直して」「テストを追加して」とターミナルに打つだけ。開発者の反復作業を大幅に削減。CLIがオープンソースなのでカスタマイズも自由。",
    features: ["オープンソースCLI", "GPT-4o搭載", "マルチモーダル入力", "長時間自律実行", "Hooks対応"],
    pricing: "Plus $20/月 / Pro $200/月 / API従量課金",
    url: "https://github.com/openai/codex",
    icon: <Code className="h-6 w-6" />,
  },

  // ── カテゴリ3: AI検索・リサーチ ──────────────────────────────
  {
    id: 11,
    name: "Perplexity AI",
    category: "AI検索・リサーチ",
    description: "「回答に必ず出典がつくAI検索」。Webをリアルタイム検索し、情報源のリンク付きで回答。Pro Searchで深掘り調査、Pagesでリサーチ結果をそのまま記事化できます。",
    purpose: "市場調査・競合分析・技術調査で「信頼できるソース付き」の情報が欲しいときに。Google検索の代替として日常使いできます。",
    features: ["引用付き回答", "Pro Search(深掘り)", "Pages(記事化)", "ファイルアップロード", "リアルタイム検索"],
    pricing: "無料 / Pro $20/月 / Max $200/月",
    url: "https://perplexity.ai",
    icon: <Search className="h-6 w-6" />,
  },
  {
    id: 12,
    name: "Genspark",
    category: "AI検索・リサーチ",
    description: "「検索から実行まで一気通貫のAIエージェント」。複数のAIが協調して検索・分析し、Sparkpagesで結果を自動整理。Super Agentは電話予約やスライド作成まで代行します。",
    purpose: "「調べて→まとめて→資料にして→予約までして」を1ツールで完結。営業準備やイベント手配の手間を劇的に減らせます。",
    features: ["Super Agent", "Sparkpages", "電話予約代行", "スライド自動生成", "8つのLLM協調"],
    pricing: "無料 / Plus $24.99/月 / Pro $249.99/月",
    url: "https://genspark.ai",
    icon: <Search className="h-6 w-6" />,
  },
  {
    id: 13,
    name: "Felo",
    category: "AI検索・リサーチ",
    description: "「多言語リサーチとプレゼン作成を同時にこなす」。日本語で質問して英語・中国語の情報源も自動検索。検索結果をマインドマップやスライドにワンクリック変換します。",
    purpose: "海外市場の調査や、外国語の技術情報を日本語で理解したいとき。調べた内容をそのまま社内プレゼンにできるのが強みです。",
    features: ["多言語横断検索", "マインドマップ生成", "プレゼン自動作成", "翻訳統合", "引用付き回答"],
    pricing: "無料版あり / Pro $14.99/月",
    url: "https://felo.ai",
    icon: <Globe className="h-6 w-6" />,
  },
  {
    id: 14,
    name: "Grok",
    category: "AI検索・リサーチ",
    description: "「SNSのリアルタイム情報に強いAI」。X(旧Twitter)と直結し、今この瞬間の話題やトレンドを分析。Aurora画像生成やマルチエージェント推論も搭載しています。",
    purpose: "SNS上の評判調査、トレンド把握、競合のSNS動向分析に。リアルタイム性が求められるマーケティング・広報業務に強いです。",
    features: ["X(Twitter)連携", "リアルタイム分析", "Aurora画像生成", "マルチエージェント", "DeepSearch"],
    pricing: "無料 / SuperGrok $30/月",
    url: "https://x.ai/grok",
    icon: <Zap className="h-6 w-6" />,
  },
  {
    id: 105,
    name: "ChatGPT o3 / o4",
    category: "AI検索・リサーチ",
    description: "「推論に特化したOpenAIの高性能モデル」。o3・o4-miniは数学・科学・コーディング・論理パズルで従来モデルを大きく超える精度を発揮。複雑な問題を段階的に推論して正解を導きます。",
    purpose: "難易度の高い技術問題・戦略分析・法的文書解釈など、深い推論が必要な業務に。通常のChatGPTでは解決しにくい複雑なタスクで威力を発揮します。",
    features: ["高度推論能力", "数学・科学特化", "コーディング問題解決", "o4-mini(コスト最適化)", "Web検索統合"],
    pricing: "Plus $20/月〜 / Pro $200/月 / API従量課金",
    url: "https://openai.com",
    icon: <Brain className="h-6 w-6" />,
  },

  // ── カテゴリ4: AIコーディング ─────────────────────────────────
  {
    id: 15,
    name: "VS Code + GitHub Copilot",
    category: "AIコーディング",
    description: "「世界で最も使われているエディタにAIが標準搭載」。Microsoft製の無料コードエディタにGitHub Copilotが統合され、コード補完・チャット・エージェント機能がエディタ内で完結。拡張機能の生態系も圧倒的。",
    purpose: "プログラミングの入口として、またプロの日常ツールとして。無料でAIコーディングを始めたいなら、まずここから。拡張機能で好みの環境に仕上げられます。",
    features: ["GitHub Copilot統合", "AIチャット", "エージェント機能", "数万の拡張機能", "全言語対応"],
    pricing: "VS Code無料 / Copilot Free無料 / Pro $10/月 / Pro+ $39/月",
    url: "https://code.visualstudio.com",
    icon: <Monitor className="h-6 w-6" />,
  },
  {
    id: 17,
    name: "Cursor",
    category: "AIコーディング",
    description: "「エディタ自体がAIエンジニア」。コードベース全体を理解した上でComposerが複数ファイルを同時編集。GPT-4o、Claude、Geminiなど好みのAIを切替えて使えます。",
    purpose: "「この機能を追加して」と自然言語で指示するだけで、関連する複数ファイルを一括修正。プロトタイプの爆速開発に。",
    features: ["Composer(マルチファイル)", "コードベース理解", "複数AI切替", "Tab補完", "ターミナル統合"],
    pricing: "無料 / Pro $20/月 / Pro+ $60/月 / Ultra $200/月",
    url: "https://cursor.com",
    icon: <Code className="h-6 w-6" />,
  },
  {
    id: 18,
    name: "Windsurf",
    category: "AIコーディング",
    description: "「流れるように開発が進むAIエディタ」。Cascadeエンジンがプロジェクト全体の文脈を把握し、的確なコード提案を実現。ワンクリックデプロイまで完結。",
    purpose: "個人開発者や小規模チームが、開発からデプロイまでを最短で回したいとき。コンテキスト理解の精度が高く、的外れな提案が少ないのが特徴。",
    features: ["Cascadeエンジン", "Supercomplete", "デプロイ連携", "マルチファイル編集", "コンテキスト認識"],
    pricing: "無料 / Pro $15/月 / Teams $30/席/月",
    url: "https://windsurf.com",
    icon: <Code className="h-6 w-6" />,
  },
  {
    id: 19,
    name: "Devin",
    category: "AIコーディング",
    description: "「タスクを丸ごと任せられる自律型AIエンジニア」。仕様を伝えるだけで設計・実装・デバッグ・PRまで一貫して自律実行。人間はレビューだけでOKです。",
    purpose: "「この機能、誰かに任せたい」を実現。人手不足のチームや、定型的な開発タスクの外注代替として注目されています。",
    features: ["完全自律開発", "設計→実装→テスト", "PR自動作成", "ブラウザ・ターミナル操作", "Slack連携"],
    pricing: "Core $20/月 / Team $500/月",
    url: "https://devin.ai",
    icon: <Bot className="h-6 w-6" />,
  },
  {
    id: 20,
    name: "Replit Agent",
    category: "AIコーディング",
    description: "「ブラウザだけでアプリが完成する」。自然言語で要件を伝えると、環境構築からコーディング・デプロイまで全自動。プログラミング初心者でもWebアプリを作れます。",
    purpose: "開発環境を用意する手間をゼロに。アイデアを形にするまでの距離が最も短いツール。学習目的にも最適です。",
    features: ["ブラウザ完結", "自然言語→アプリ", "自動デプロイ", "DB/API統合", "リアルタイムコラボ"],
    pricing: "Free無料 / Replit Core $25/月",
    url: "https://replit.com",
    icon: <Code className="h-6 w-6" />,
  },
  {
    id: 106,
    name: "Gemini CLI",
    category: "AIコーディング",
    description: "「ターミナルから直接Geminiと対話するGoogle製AIエージェント」。コマンドラインで開発タスクを実行・ファイル操作・Web検索・コード生成が可能。Claude Codeと同様のコンセプトでGeminiの知性をローカルに。",
    purpose: "Google Geminiを使い慣れているエンジニアがCLIで開発効率を上げたいとき。ターミナルを離れずにAIアシスタントを活用できます。",
    features: ["ターミナル完結", "Gemini搭載", "ファイル操作自動化", "Web検索統合", "コード生成・修正"],
    pricing: "無料(Gemini APIキー必要) / AI Pro $19.99/月",
    url: "https://github.com/google-gemini/gemini-cli",
    icon: <Code className="h-6 w-6" />,
  },

  // ── カテゴリ5: ビルドエージェント ─────────────────────────────
  {
    id: 21,
    name: "Bolt.new",
    category: "ビルドエージェント",
    description: "「自然言語でフルスタックアプリを即座に構築」。StackBlitz発のAI開発環境。ブラウザ内でフロントエンド・バックエンド・DBをまとめて生成し、そのままデプロイ。",
    purpose: "「こんなWebアプリがほしい」と伝えるだけで動くプロトタイプが完成。LP・社内ツール・MVPを最速で形にしたいビジネスパーソンに。",
    features: ["フルスタック自動生成", "ブラウザ内実行", "即時デプロイ", "複数フレームワーク対応", "リアルタイムプレビュー"],
    pricing: "Free無料 / Pro $20/月 / Team $40/月",
    url: "https://bolt.new",
    icon: <Layout className="h-6 w-6" />,
  },
  {
    id: 22,
    name: "Lovable",
    category: "ビルドエージェント",
    description: "「アイデアを話すだけで本格的なWebアプリに」。Supabase(DB)・Stripe(決済)との連携が組み込まれており、本番運用レベルのアプリを自然言語だけで構築できます。",
    purpose: "SaaSのMVP開発やスタートアップの初期プロダクト作りに。DB・認証・決済まで含んだ「すぐ使えるアプリ」を非エンジニアでも作れます。",
    features: ["自然言語→フルスタック", "Supabase/Stripe統合", "GitHub連携", "リアルタイム編集", "本番デプロイ"],
    pricing: "Free無料 / Starter $25/月 / Launch $50/月 / Scale $100/月",
    url: "https://lovable.dev",
    icon: <Layout className="h-6 w-6" />,
  },
  {
    id: 23,
    name: "Vercel v0",
    category: "ビルドエージェント",
    description: "「テキストからプロ品質のUIコンポーネントを生成」。React/Next.js + shadcn/ui + Tailwindで構成された、コピペ可能なコードを出力。Figmaデザインの取り込みにも対応。",
    purpose: "デザインカンプをコードに変換したい、UIのプロトタイプを素早く作りたいフロントエンドの現場に。デザイナーとエンジニアの橋渡し役。",
    features: ["UI自動生成", "React/Next.js対応", "shadcn/ui・Tailwind", "Figmaインポート", "コードエクスポート"],
    pricing: "Free 200クレジット/月 / Premium $20/月",
    url: "https://v0.dev",
    icon: <Layout className="h-6 w-6" />,
  },

  // ── カテゴリ6: 業務自動化 ────────────────────────────────────
  {
    id: 25,
    name: "Microsoft Copilot",
    category: "業務自動化",
    description: "「Word・Excel・PowerPoint・TeamsをAIが操る」。Microsoft 365に完全統合され、データ分析・資料作成・メール要約・会議記録をAIが代行。日常業務の最強パートナー。",
    purpose: "Office製品を日常的に使う企業なら最大効果。Excel分析の自動化、PowerPoint生成、Teamsの会議要約が劇的に楽になります。",
    features: ["Microsoft 365統合", "Excel分析自動化", "PowerPoint生成", "Teams会議要約", "Outlookメール要約"],
    pricing: "Copilot Pro ¥3,200/月 / M365 Copilot $30/席/月(M365別途)",
    url: "https://copilot.microsoft.com",
    icon: <BarChart className="h-6 w-6" />,
  },
  {
    id: 26,
    name: "Zapier",
    category: "業務自動化",
    description: "「7,000以上のアプリをAIでつなぐ自動化の王道」。Gmail→Slack通知、フォーム回答→スプレッドシート記録など、繰り返し作業をノーコードで自動化。AI判断ノードも搭載。",
    purpose: "「毎日同じ作業を手でやっている」すべての業務人に。部署・職種を問わず、定型業務の自動化で月に何十時間も節約できます。",
    features: ["7,000+アプリ連携", "AIワークフロー", "ノーコード構築", "条件分岐", "スケジュール実行"],
    pricing: "Free無料 / Starter $29.99/月 / Professional $73.50/月",
    url: "https://zapier.com",
    icon: <Bot className="h-6 w-6" />,
  },
  {
    id: 27,
    name: "n8n",
    category: "業務自動化",
    description: "「自社サーバーで動かせるオープンソース自動化」。400以上の連携にAIノードを組み合わせて複雑なワークフローを構築。データを外部に出さずに自動化できます。",
    purpose: "セキュリティ要件が厳しい企業や、Zapierでは対応しきれない複雑な自動化に。自社インフラで完全にコントロールできます。",
    features: ["400+連携", "AIノード搭載", "セルフホスト可", "無制限ワークフロー", "カスタムコード"],
    pricing: "セルフホスト無料 / Cloud Starter €24/月",
    url: "https://n8n.io",
    icon: <Bot className="h-6 w-6" />,
  },
  {
    id: 28,
    name: "Dify",
    category: "業務自動化",
    description: "「社内AIチャットボットをノーコードで開発」。社内文書をRAGで取り込み、自社専用のAIアシスタントを構築。ビジュアルワークフローで複雑なAIパイプラインも設計可能。",
    purpose: "「社内FAQボットを作りたい」「顧客対応を自動化したい」ときに。オープンソースでセルフホストできるため、機密データも安全に扱えます。",
    features: ["ビジュアルワークフロー", "RAGエンジン", "MCP対応", "50+組込みツール", "セルフホスト/SaaS"],
    pricing: "セルフホスト無料 / Cloud Free〜",
    url: "https://dify.ai",
    icon: <Bot className="h-6 w-6" />,
  },
  {
    id: 29,
    name: "Make",
    category: "業務自動化",
    description: "「複雑な業務フローを図で組み立てる」。直感的なビジュアルエディタで分岐・ループ・エラー処理まで設計でき、Zapierより複雑なシナリオに対応します。",
    purpose: "マーケティングオートメーション、受注→請求→在庫管理の連携など、多段階の業務プロセスを丸ごと自動化したいときに。",
    features: ["ビジュアルエディタ", "複雑な分岐・ループ", "1,800+アプリ連携", "データ変換", "エラーハンドリング"],
    pricing: "Free無料 / Core $10.59/月 / Pro $18.82/月",
    url: "https://make.com",
    icon: <Bot className="h-6 w-6" />,
  },

  // ── カテゴリ7: 画像・デザイン ────────────────────────────────
  {
    id: 30,
    name: "Midjourney",
    category: "画像・デザイン",
    description: "「最高品質のAI画像生成」。v7でテキスト描画精度が飛躍的に向上。スタイル参照やドラフトモードで、ブランドの世界観を一貫した画像で表現できます。",
    purpose: "広告クリエイティブ、商品コンセプトビジュアル、ブランドイメージの作成に。デザイナーのアイデア出しツールとしても活躍。",
    features: ["v7高精度生成", "テキスト描画", "スタイル参照", "高速ドラフト", "WebアプリUI"],
    pricing: "Basic $10/月 / Standard $30/月 / Pro $60/月",
    url: "https://midjourney.com",
    icon: <Palette className="h-6 w-6" />,
  },
  {
    id: 31,
    name: "DALL-E 3 / GPT Image",
    category: "画像・デザイン",
    description: "「ChatGPTの会話の中で画像を作って編集できる」。自然な日本語の指示を正確に反映し、生成した画像をその場で修正・バリエーション展開。ChatGPTとの統合が最大の強み。",
    purpose: "プレゼン資料に挿絵がほしい、SNS投稿用の画像が必要——ChatGPTを使いながら「ついでに画像も」で済むお手軽さが魅力。",
    features: ["ChatGPT統合", "画像編集", "テキスト描画", "商用利用可", "バリエーション生成"],
    pricing: "ChatGPT Plus $20/月 / Pro $200/月",
    url: "https://openai.com/dall-e-3",
    icon: <Palette className="h-6 w-6" />,
  },
  {
    id: 32,
    name: "Canva AI (Magic Studio)",
    category: "画像・デザイン",
    description: "「デザイン初心者でもプロ品質の素材を作れる」。AI画像生成・背景除去・動画編集・プレゼン作成が一つのプラットフォームに。テンプレートの豊富さは圧倒的。",
    purpose: "デザイナーがいないチームの「デザイン全般の駆け込み寺」。SNS画像、チラシ、名刺、動画サムネイルまで何でも対応できます。",
    features: ["Magic Studio(AI統合)", "背景除去", "テンプレート数万種", "動画編集", "ブランドキット"],
    pricing: "Free無料 / Pro ¥1,500/月 / Teams ¥1,800/月",
    url: "https://canva.com",
    icon: <Palette className="h-6 w-6" />,
  },
  {
    id: 33,
    name: "Adobe Firefly",
    category: "画像・デザイン",
    description: "「著作権を気にせず商用利用できるAI画像生成」。Adobe Stock等の権利クリアなデータで学習。Photoshop・Illustratorとの連携で既存ワークフローにそのまま組み込めます。",
    purpose: "広告・パッケージ・Webデザインなど、著作権リスクを避けたい商用利用に。Adobe製品ユーザーはシームレスに導入できます。",
    features: ["商用利用安全", "Creative Cloud統合", "テキストエフェクト", "画像拡張", "スタイル参照"],
    pricing: "Firefly Standardプラン ¥680/月 / Creative Cloud同梱",
    url: "https://firefly.adobe.com",
    icon: <Palette className="h-6 w-6" />,
  },

  // ── カテゴリ8: 動画・音声 ────────────────────────────────────
  {
    id: 34,
    name: "Runway ML",
    category: "動画・音声",
    description: "「テキストや画像から映像を生成・編集」。Gen-4でキャラクターや背景の一貫性が大幅向上。Act-One(表情転写)や音声生成も搭載し、動画制作の全工程をカバー。",
    purpose: "プロモーション動画やSNSコンテンツを、撮影なしで制作。既存動画の編集・エフェクト追加にも使えるオールインワンツール。",
    features: ["Gen-4動画生成", "Act-One(表情転写)", "音声生成", "一貫性保持", "動画編集"],
    pricing: "Free無料 / Standard $12/月 / Pro $28/月",
    url: "https://runwayml.com",
    icon: <Video className="h-6 w-6" />,
  },
  {
    id: 35,
    name: "ElevenLabs",
    category: "動画・音声",
    description: "「本物と区別がつかないAI音声を70以上の言語で」。テキストを入力するだけで自然な音声に変換。自分の声をクローンして多言語ナレーションを作ることも可能です。",
    purpose: "ナレーション外注費の削減、多言語コンテンツの内製化、音声ガイドの制作に。Eラーニングや社内研修動画の音声もすぐ作れます。",
    features: ["高品質音声合成", "70+言語対応", "音声クローン", "リアルタイム変換", "API提供"],
    pricing: "Free無料 / Starter $5/月 / Creator $22/月",
    url: "https://elevenlabs.io",
    icon: <Video className="h-6 w-6" />,
  },
  {
    id: 36,
    name: "HeyGen",
    category: "動画・音声",
    description: "「AIアバターが自分の代わりに話す動画を生成」。100以上のアバターと多言語リップシンクで、顔出し不要のプロモーション動画を作成。インタラクティブアバターでリアルタイム対話も。",
    purpose: "製品紹介・教育動画・多言語マーケティングに。「1本の動画を撮れば、30言語の吹替版が自動生成」で海外展開を加速。",
    features: ["100+AIアバター", "多言語リップシンク", "インタラクティブアバター", "自分のクローン", "テンプレート豊富"],
    pricing: "Free無料 / Creator $24/月 / Business $39/席/月〜",
    url: "https://heygen.com",
    icon: <Users className="h-6 w-6" />,
  },
  {
    id: 37,
    name: "Loom AI",
    category: "動画・音声",
    description: "「画面録画＋AIで業務連絡を動画に」。録画した動画をAIが自動要約・文字起こし・ハイライト抽出。「テキストで5分かかる説明を、1分の動画で伝える」非同期コミュニケーションツール。",
    purpose: "リモートワークでの業務連絡、製品デモ、手順説明に。会議を減らし、「動画メモ」で非同期に情報共有できます。",
    features: ["画面録画", "AI自動要約", "文字起こし", "ハイライト抽出", "チーム共有"],
    pricing: "Free無料 / Business $15/席/月 / Enterprise 要問合せ",
    url: "https://loom.com",
    icon: <Video className="h-6 w-6" />,
  },

  // ── カテゴリ9: ドキュメント・効率化 ──────────────────────────
  {
    id: 38,
    name: "Gamma",
    category: "ドキュメント・効率化",
    description: "「テキストを貼るだけでプレゼン資料が完成」。AIが構成・デザイン・レイアウトを自動生成。スライド・ドキュメント・Webページの3形式に対応し、共有もワンクリック。",
    purpose: "提案書・報告書・社内プレゼンの作成時間を1/10に。「内容は決まっているけどデザインに時間がかかる」問題を解決します。",
    features: ["AI自動デザイン", "テキスト→スライド", "Webページ出力", "チーム共有", "テンプレート豊富"],
    pricing: "Free無料 / Plus $10/月 / Pro $20/月",
    url: "https://gamma.app",
    icon: <FileText className="h-6 w-6" />,
  },
  {
    id: 39,
    name: "Notion AI",
    category: "ドキュメント・効率化",
    description: "「チームのナレッジベースにAIが宿る」。Notion内の全ドキュメントをAIが横断検索・要約・翻訳。議事録の自動生成やタスクの自動整理で、チーム全体の情報管理を効率化します。",
    purpose: "Notionをチームで使っているなら必須。「あの資料どこだっけ？」がAIへの質問で解決。議事録・日報・WIKIの作成もAIが補助。",
    features: ["全ドキュメント横断検索", "文章生成・要約", "翻訳", "Q&A", "タスク自動整理"],
    pricing: "Free / Plus $12/月 / Business $18/席/月(AI込み)",
    url: "https://notion.so",
    icon: <FileText className="h-6 w-6" />,
  },
  {
    id: 40,
    name: "Napkin AI",
    category: "ドキュメント・効率化",
    description: "「テキストを貼ると図解・インフォグラフィックに変換」。文章の構造を分析し、フローチャート・図表・ビジュアルを自動生成。ブログ記事やプレゼンの「見映え」を一瞬で改善。",
    purpose: "「文字だけの企画書を図解入りにしたい」「ブログに挿絵が欲しい」ときに。デザインスキル不要で、伝わる資料を作れます。",
    features: ["テキスト→図解変換", "インフォグラフィック", "フローチャート", "スタイルカスタマイズ", "エクスポート"],
    pricing: "Free無料 / Pro $15/月",
    url: "https://napkin.ai",
    icon: <FileText className="h-6 w-6" />,
  },
  {
    id: 41,
    name: "DeepL",
    category: "ドキュメント・効率化",
    description: "「ニュアンスまで正確に訳すAI翻訳」。ビジネス文書の翻訳品質で業界トップクラス。Write機能でAIが文章を改善し、用語集カスタマイズで社内用語にも対応します。",
    purpose: "海外取引のメール・契約書の翻訳、多言語ドキュメント作成に。Google翻訳では不十分なビジネスシーンで真価を発揮します。",
    features: ["高精度翻訳", "Write(文章改善)", "用語集カスタマイズ", "ファイル翻訳", "30+言語対応"],
    pricing: "Free無料 / Starter $8.74/月 / Advanced $28.74/月 / Ultimate $57.49/月",
    url: "https://deepl.com",
    icon: <Globe className="h-6 w-6" />,
  },
  {
    id: 42,
    name: "Otter.ai",
    category: "ドキュメント・効率化",
    description: "「会議に参加させるだけで議事録が完成」。Zoom・Teams・Google Meetに自動参加し、リアルタイムで文字起こし・話者識別・要約・アクションアイテム抽出。英語会議の理解補助にも。",
    purpose: "「議事録を書く時間がもったいない」すべてのビジネスパーソンに。会議後すぐに要約とToDoが共有され、次のアクションに集中できます。",
    features: ["自動文字起こし", "話者識別", "AI要約", "アクション抽出", "Zoom/Teams/Meet連携"],
    pricing: "Free無料 / Pro $8.33〜16.99/月 / Business $20〜30/月",
    url: "https://otter.ai",
    icon: <FileText className="h-6 w-6" />,
  },

  // ── カテゴリ10: バックエンド・インフラ ──────────────────────
  {
    id: 45,
    name: "Supabase",
    category: "バックエンド・インフラ",
    description: "「Firebaseのオープンソース代替」。PostgreSQLベースのBaaSで、データベース・認証・ストレージ・Edge Functions・リアルタイム通信を統合提供。RLS（行レベルセキュリティ）やSQL直接操作も可能で、ベンダーロックインが少ない。",
    purpose: "バックエンドをゼロから構築する手間を大幅に削減。認証・DB・ファイル保存をひとつのダッシュボードで管理でき、スモールチームでも本格的なWebアプリを素早くリリースできます。",
    features: ["PostgreSQL DB", "認証/RLS", "ストレージ", "Edge Functions", "リアルタイム通信", "ベクトル検索対応"],
    pricing: "Free $0 / Pro $25/月 / Team $599/月 / Enterprise カスタム",
    url: "https://supabase.com",
    icon: <Database className="h-6 w-6" />,
  },
  {
    id: 46,
    name: "Cloudflare Workers",
    category: "バックエンド・インフラ",
    description: "「世界300拠点のエッジで動くサーバーレス基盤」。Workers（バックエンド）、Pages（フロント）、D1（SQLite DB）、R2（S3互換ストレージ・エグレス無料）を統合。転送量無料が最大の強みで、コストを劇的に抑えられます。",
    purpose: "グローバルに高速なAPIやWebサイトを低コストで運用したい企業に最適。R2のエグレス無料はAWS S3と比較して大幅なコスト削減効果があります。",
    features: ["エッジ実行(300拠点)", "D1(SQLite DB)", "R2(エグレス無料)", "Workers AI", "Pages(自動デプロイ)", "Durable Objects"],
    pricing: "Free 10万リクエスト/日 / Paid $5/月〜 / R2 エグレス無料",
    url: "https://developers.cloudflare.com",
    icon: <Cloud className="h-6 w-6" />,
  },
  {
    id: 47,
    name: "Vercel",
    category: "バックエンド・インフラ",
    description: "「Git pushだけで世界にデプロイ」。Next.jsの開発元が提供するホスティングプラットフォーム。自動ビルド→CDNデプロイ→プレビューURL生成のワークフローを実現。v0（AIによるUI自動生成）も統合。",
    purpose: "Next.js/React/Vue等のフロントエンドアプリを最速でデプロイ。プレビューURLでチームレビューが簡単に。v0でUIプロトタイプを即生成できます。",
    features: ["Git連携自動デプロイ", "Fluid Compute", "プレビューURL", "v0(AI UI生成)", "ISR/SSR対応", "Analytics"],
    pricing: "Hobby $0(個人) / Pro $20/ユーザー/月 / Enterprise カスタム",
    url: "https://vercel.com",
    icon: <Globe className="h-6 w-6" />,
  },
  {
    id: 50,
    name: "GitHub",
    category: "バックエンド・インフラ",
    description: "「世界最大のコード共有・CI/CDプラットフォーム」。バージョン管理(Git)の中心地であり、GitHub Actions(CI/CD自動化)・GitHub Copilot(AIコーディング)・Codespaces(クラウド開発環境)・Advanced Security(脆弱性検知)を統合。世界1億人超の開発者が利用。",
    purpose: "コードの管理・チームコラボレーション・自動テスト・デプロイの起点。AI駆動開発でも「コードの最終保管場所＋自動化のトリガー」として不可欠です。",
    features: ["Git バージョン管理", "GitHub Actions(CI/CD)", "GitHub Copilot統合", "Codespaces(クラウドIDE)", "Advanced Security", "Pull Request / Code Review"],
    pricing: "Free / Pro $4/月 / Team $4/ユーザー/月 / Enterprise カスタム",
    url: "https://github.com",
    icon: <Code className="h-6 w-6" />,
  },

  // ── カテゴリ11: 日本のAI ──────────────────────────────────────
  {
    id: 201,
    name: "Sakana AI（魚AI）",
    category: "日本のAI",
    description: "「日本発AIユニコーン・Sakana Fugu公開」。東京拠点のAIスタートアップ（共同創業者: Llion Jones・David Ha、元Google Brain）。2026年6月に複数AIを統合する「Sakana Fugu」を正式公開。プログラミングベンチマークでClaude Fable・GPT-5.5を上回る性能を達成。",
    purpose: "国産・日本語ネイティブのAI開発パートナーを求める企業・研究者向け。評価額4,000億円超のユニコーン企業として海外勢と互角の技術力を持ちます。",
    features: ["Sakana Fugu(マルチモデル統合)", "AI Scientist(自律研究)", "進化的アルゴリズム", "防衛・バイオ分野実績", "TinySwallow日本語LLM"],
    pricing: "研究API: 一部公開 / Sakana Fugu API: 問合せ",
    url: "https://sakana.ai",
    icon: <Fish className="h-6 w-6" />,
  },
  {
    id: 202,
    name: "PLaMo 3.0 Prime（Preferred Networks）",
    category: "日本のAI",
    description: "「国産フルスクラッチLLM・デジタル庁源内採用」。Preferred Networks（東京）開発。2026年6月に最新版PLaMo 3.0 Primeを正式リリース。デジタル庁の生成AI基盤「源内（Gennai）」に採用された唯一の国産フルスクラッチモデル。256Kコンテキスト対応。",
    purpose: "日本語業務文書・行政用途に特化。データが国内に閉じるオンプレ運用にも対応しており、医療・金融・法務など機密性の高い業種に最適です。",
    features: ["日本語フルスクラッチ", "256Kトークン対応", "デジタル庁源内採用", "Reasoningモード対応", "低価格(¥60/MTok)"],
    pricing: "Standard: 入力¥60/出力¥250/MTok / エンタープライズ 要問合せ",
    url: "https://plamo.preferredai.jp",
    icon: <Brain className="h-6 w-6" />,
  },
  {
    id: 203,
    name: "rinna",
    category: "日本のAI",
    description: "「日本語特化の会話・キャラクターAI」。rinna株式会社（旧Microsoft日本語AI部門スピンオフ）が開発。LINE/SNSに特化したキャラクターAIから業務用途の日本語LLMまで幅広く提供。",
    purpose: "カスタマーサポートボット、SNSキャラクター、社内問い合わせ対応など、日本語ネイティブの会話体験が求められる業務に。OSS版も公開されており試しやすい。",
    features: ["日本語会話特化", "キャラクターAI", "LINE/SNS対応", "OSS版公開", "API提供"],
    pricing: "OSS版無料 / エンタープライズAPI 要問合せ",
    url: "https://rinna.co.jp",
    icon: <Bot className="h-6 w-6" />,
  },

  // ── カテゴリ12: 中国系AI ─────────────────────────────────────
  {
    id: 301,
    name: "DeepSeek V4 / R1",
    category: "中国系AI",
    isChina: true,
    description: "「圧倒的コスト効率を誇るオープンソースLLM」。中国DeepSeek社開発。2026年4月に1.6兆パラメータのV4-Proを公開。SWE-bench 80.6%でClaude Opus比1/6のコストを実現。R1は推論特化モデル。2025年初頭にAI業界を震撼させた注目株。",
    purpose: "コスト重視のAI開発者・研究者向け。オープンウェイト版を自社サーバーで動かすことも可能で、機密データをクラウドに出さない運用も選択できます。",
    features: ["GPT-5.4水準の性能", "低コスト推論(Opus比1/6)", "R1(高度推論特化)", "オープンウェイト版あり", "V4-Pro(1.6Tパラメータ)"],
    pricing: "API V4: 低価格 / OSS版は自社運用可",
    url: "https://deepseek.com",
    icon: <Brain className="h-6 w-6" />,
  },
  {
    id: 302,
    name: "Kimi K2（Moonshot AI）",
    category: "中国系AI",
    isChina: true,
    description: "「1兆パラメータのコーディング特化LLM」。中国Moonshot AI社開発。2026年6月に「Kimi K2.7-Code」を公開。262Kコンテキスト、SWE-bench 84%超でコーディングエージェント性能がトップクラス。300エージェントを12時間以上自律実行した実績あり。",
    purpose: "大規模コーディング・長文ドキュメント分析に最適。API料金が他社の1/18程度と格安。機密情報の入力は厳禁で、セルフホスト（国内サーバー）推奨。",
    features: ["262Kトークン対応", "コーディングエージェント特化", "300エージェント並列実行", "マルチモーダル対応", "Hugging Face公開"],
    pricing: "API: 入力$0.95/出力$4.00/MTok / Hugging Face OSS版あり",
    url: "https://kimi.ai",
    icon: <FileText className="h-6 w-6" />,
  },
  {
    id: 303,
    name: "Qwen（通義千問）",
    category: "中国系AI",
    isChina: true,
    description: "「アリババ製の多言語・マルチモーダルLLM」。アリババグループ開発のQwenシリーズ。日本語を含む多言語に対応し、テキスト・画像・音声のマルチモーダル処理が可能。Qwen2.5-Max等のオープンウェイト版が広く利用されています。",
    purpose: "中国語ビジネス文書の処理や、アジア多言語対応が必要な業務に。オープンウェイト版を自社環境で動かすことも可能ですが、ライセンス・利用規約の確認が必要です。",
    features: ["多言語対応(日本語含む)", "マルチモーダル", "オープンウェイト版あり", "Qwen3-Coder(コーディング特化)", "API提供"],
    pricing: "API従量課金 / オープンウェイト版は自社運用可",
    url: "https://qwenlm.github.io",
    icon: <Globe className="h-6 w-6" />,
  },
  {
    id: 304,
    name: "Manus（マナス）",
    category: "中国系AI",
    isChina: true,
    description: "「完全自律型AIエージェント」。中国Monica.im社開発。2025年3月のデモ動画公開後にAI業界を震撼させた自律型エージェント。テキスト指示だけでWebアプリ構築・デプロイを完全自動化。マルチエージェント構造でClaude/GPT/Geminiを自動選択して実行します。",
    purpose: "複数ステップの複雑な業務を丸ごと自動化したい場合に。Webリサーチ・スプレッドシート作成・コード実装を連続実行。2026年現在一般公開中。",
    features: ["完全自律タスク実行", "マルチエージェント構造", "ブラウザ操作自動化", "デスクトップアプリ対応", "日本語対応"],
    pricing: "無料(1-3タスク/日) / Plus $39/月",
    url: "https://manus.im/ja",
    icon: <Bot className="h-6 w-6" />,
  },
];

// ---------------------------------------------------------------------------
// ツール定義（英語）
// ---------------------------------------------------------------------------
const aiToolsEn: {
  id: number;
  name: string;
  category: string;
  description: string;
  purpose: string;
  features: string[];
  pricing: string;
  url: string;
  icon: React.ReactNode;
  isChina?: boolean;
}[] = [
  // ── Category 1: Big 3 AI Platforms ──────────────────────────
  {
    id: 1,
    name: "ChatGPT",
    category: "Big3AIplatforms",
    description: "Your all-purpose AI assistant for everything. Write documents, generate images, have voice conversations, search the web, and analyze data — all from one interface. Create your own custom AI with GPTs.",
    purpose: "The perfect starting point if you don't know where to begin. From drafting daily reports and emails to generating images, you can handle it all with one tool.",
    features: ["Multimodal support", "Image generation (GPT Image)", "Voice conversation", "GPTs / Custom AI", "Web search integration"],
    pricing: "Free / Go $8/mo / Plus $20/mo / Pro $200/mo",
    url: "https://chat.openai.com",
    icon: <Brain className="h-6 w-6" />,
  },
  {
    id: 2,
    name: "Gemini",
    category: "Big3AIplatforms",
    description: "AI that supercharges your entire Google suite. Deeply integrated with Gmail, Docs, Sheets, and Drive. Processes up to 2M tokens in one go, and Deep Research auto-generates expert-level reports.",
    purpose: "If your company runs on Google Workspace, the impact is enormous. Summarize emails, analyze spreadsheets, and cross-search large PDF collections in one click.",
    features: ["2M token processing", "Google Workspace integration", "Deep Research", "100+ languages", "Video understanding"],
    pricing: "Free / AI Pro $19.99/mo (2TB included) / AI Ultra — contact for pricing",
    url: "https://gemini.google.com",
    icon: <Brain className="h-6 w-6" />,
  },
  {
    id: 3,
    name: "Claude",
    category: "Big3AIplatforms",
    description: "The accurate, safety-conscious AI that excels at long documents. Processes 200K tokens with precision. Artifacts let you generate code and documents on the spot, and Computer Use automates PC tasks.",
    purpose: "Ideal for work where accuracy is paramount — contracts, legal documents, and technical specs. Feed it your internal documents via Projects and it acts as a dedicated assistant.",
    features: ["200K token processing", "Projects (knowledge vault)", "Artifacts", "Computer Use", "High accuracy"],
    pricing: "Free / Pro $20/mo / Max $100–200/mo / Team $25–30/seat/mo",
    url: "https://claude.ai",
    icon: <Zap className="h-6 w-6" />,
  },

  // ── Category 2: Claude Ecosystem ──────────────────────────────
  {
    id: 101,
    name: "Claude Code",
    category: "ClaudeEcosystem",
    description: "The AI engineer living in your terminal. With a 1M-token context, it understands your entire project and handles multi-file editing, Git operations, and test execution in one shot. Sub-agents for parallel work and MCP server integrations extend it further.",
    purpose: "Dramatically accelerates the write → test → commit cycle. Completes large-scale refactors and reviews in minutes. Build custom automations with Hooks.",
    features: ["1M token context", "Parallel sub-agents", "Git automation", "MCP extensions", "Hooks support", "Structured output"],
    pricing: "Pro $20/mo / Max $100–200/mo / Team Premium $150/seat/mo",
    url: "https://docs.anthropic.com/en/docs/claude-code",
    icon: <Code className="h-6 w-6" />,
  },
  {
    id: 102,
    name: "Claude Fable (claude-fable-5)",
    category: "ClaudeEcosystem",
    description: "Anthropic's latest flagship model. A next-generation model built for complex reasoning, long-running tasks, and advanced coding. Available as Claude Code's default — the perfect foundation for AI-driven development.",
    purpose: "For design reviews, architecture decisions, and complex bug analysis where precision matters. Deeper reasoning than previous models solves tough business problems.",
    features: ["Advanced reasoning", "Long-task support", "Coding specialization", "Claude Code integration", "API access"],
    pricing: "Claude API pay-as-you-go / Included in Claude Max plan",
    url: "https://www.anthropic.com",
    icon: <Sparkles className="h-6 w-6" />,
  },
  {
    id: 103,
    name: "Claude API Models (Opus / Sonnet / Haiku)",
    category: "ClaudeEcosystem",
    description: "The Claude 4 family — optimized by use case and cost. Opus 4.8 (highest quality, complex tasks), Sonnet 4.6 (balanced, general-purpose), Haiku 4.5 (fast, low-cost, high-volume) — three tiers for perfect optimization.",
    purpose: "Embed AI into your own applications. For chatbots, document summarization, classification, and code generation — choose the right model for quality and cost balance.",
    features: ["Opus 4.8 (highest quality)", "Sonnet 4.6 (balanced)", "Haiku 4.5 (fast/low-cost)", "Prompt Caching", "Tool Use / MCP support"],
    pricing: "Opus 4.8: $15/$75/MTok / Sonnet 4.6: $3/$15/MTok / Haiku 4.5: $0.8/$4/MTok",
    url: "https://www.anthropic.com/api",
    icon: <Zap className="h-6 w-6" />,
  },
  {
    id: 104,
    name: "OpenAI Codex CLI",
    category: "ClaudeEcosystem",
    description: "The AI coding agent that works from the terminal or ChatGPT. Open-source CLI plus ChatGPT integration. GPT-4o as the default model, with screenshot input and long autonomous runs supported.",
    purpose: "Just type 'fix this bug' or 'add tests' at the terminal. Eliminates repetitive developer tasks. Fully customizable since the CLI is open source.",
    features: ["Open-source CLI", "GPT-4o powered", "Multimodal input", "Long autonomous runs", "Hooks support"],
    pricing: "Plus $20/mo / Pro $200/mo / API pay-as-you-go",
    url: "https://github.com/openai/codex",
    icon: <Code className="h-6 w-6" />,
  },

  // ── Category 3: AI Search & Research ──────────────────────────
  {
    id: 11,
    name: "Perplexity AI",
    category: "AISearchResearch",
    description: "AI search that always cites its sources. Real-time web search with links to every source. Pro Search for deep-dive research, Pages to publish your findings directly as articles.",
    purpose: "When you need sourced, trustworthy information for market research, competitive analysis, or technical investigation. A daily-use alternative to Google search.",
    features: ["Cited answers", "Pro Search (deep dive)", "Pages (publish research)", "File upload", "Real-time search"],
    pricing: "Free / Pro $20/mo / Max $200/mo",
    url: "https://perplexity.ai",
    icon: <Search className="h-6 w-6" />,
  },
  {
    id: 12,
    name: "Genspark",
    category: "AISearchResearch",
    description: "An AI agent that goes from search to action in one flow. Multiple AIs collaborate to search and analyze, with Sparkpages auto-organizing results. Super Agent even makes phone reservations and builds slide decks.",
    purpose: "'Research, summarize, present, and book' all in one tool. Dramatically cuts prep time for sales calls and event planning.",
    features: ["Super Agent", "Sparkpages", "Phone reservation", "Auto slide generation", "8 LLMs collaborating"],
    pricing: "Free / Plus $24.99/mo / Pro $249.99/mo",
    url: "https://genspark.ai",
    icon: <Search className="h-6 w-6" />,
  },
  {
    id: 13,
    name: "Felo",
    category: "AISearchResearch",
    description: "Multilingual research and presentation creation in one shot. Ask in English and it auto-searches sources in other languages too. One click converts results to mind maps or slides.",
    purpose: "For researching overseas markets or understanding foreign-language technical content. Turn what you find directly into an internal presentation.",
    features: ["Cross-language search", "Mind map generation", "Auto presentation", "Translation integration", "Cited answers"],
    pricing: "Free tier available / Pro $14.99/mo",
    url: "https://felo.ai",
    icon: <Globe className="h-6 w-6" />,
  },
  {
    id: 14,
    name: "Grok",
    category: "AISearchResearch",
    description: "The AI with unmatched access to real-time social media. Directly connected to X (formerly Twitter), it analyzes trending topics this very moment. Includes Aurora image generation and multi-agent reasoning.",
    purpose: "Reputation monitoring, trend tracking, and competitor social media analysis. Excels at marketing and PR tasks that demand real-time data.",
    features: ["X (Twitter) integration", "Real-time analysis", "Aurora image generation", "Multi-agent", "DeepSearch"],
    pricing: "Free / SuperGrok $30/mo",
    url: "https://x.ai/grok",
    icon: <Zap className="h-6 w-6" />,
  },
  {
    id: 105,
    name: "ChatGPT o3 / o4",
    category: "AISearchResearch",
    description: "OpenAI's reasoning-specialized high-performance models. o3 and o4-mini dramatically outperform prior models on math, science, coding, and logic puzzles. They solve hard problems through step-by-step reasoning.",
    purpose: "For tough technical problems, strategic analysis, and legal document interpretation — anything that demands deep reasoning. Shines where regular ChatGPT struggles.",
    features: ["Advanced reasoning", "Math & science specialization", "Coding problem solving", "o4-mini (cost optimized)", "Web search integration"],
    pricing: "Plus $20/mo+ / Pro $200/mo / API pay-as-you-go",
    url: "https://openai.com",
    icon: <Brain className="h-6 w-6" />,
  },

  // ── Category 4: AI Coding & Development ───────────────────────
  {
    id: 15,
    name: "VS Code + GitHub Copilot",
    category: "AICoding",
    description: "The world's most popular editor with AI built right in. Microsoft's free code editor integrates GitHub Copilot for code completion, chat, and agent features — all within the editor. An unmatched extension ecosystem.",
    purpose: "The entry point for programming beginners and the daily driver for professionals alike. If you want AI coding for free, start here. Tune the environment with extensions.",
    features: ["GitHub Copilot integration", "AI chat", "Agent features", "Tens of thousands of extensions", "All languages supported"],
    pricing: "VS Code free / Copilot Free / Pro $10/mo / Pro+ $39/mo",
    url: "https://code.visualstudio.com",
    icon: <Monitor className="h-6 w-6" />,
  },
  {
    id: 17,
    name: "Cursor",
    category: "AICoding",
    description: "The editor that's an AI engineer itself. Composer understands your entire codebase and edits multiple files at once. Switch between GPT-4o, Claude, and Gemini as you like.",
    purpose: "Just describe a feature in plain language and watch it modify all the relevant files in one shot. Built for rapid prototyping.",
    features: ["Composer (multi-file editing)", "Codebase understanding", "Multiple AI models", "Tab completion", "Terminal integration"],
    pricing: "Free / Pro $20/mo / Pro+ $60/mo / Ultra $200/mo",
    url: "https://cursor.com",
    icon: <Code className="h-6 w-6" />,
  },
  {
    id: 18,
    name: "Windsurf",
    category: "AICoding",
    description: "The AI editor where development just flows. The Cascade engine grasps your full project context and delivers pinpoint code suggestions. One-click deployment included.",
    purpose: "For solo developers and small teams who want to ship from coding to deployment in the shortest time. High-context accuracy means fewer irrelevant suggestions.",
    features: ["Cascade engine", "Supercomplete", "Deploy integration", "Multi-file editing", "Context awareness"],
    pricing: "Free / Pro $15/mo / Teams $30/seat/mo",
    url: "https://windsurf.com",
    icon: <Code className="h-6 w-6" />,
  },
  {
    id: 19,
    name: "Devin",
    category: "AICoding",
    description: "The autonomous AI engineer you can delegate whole tasks to. Just explain the spec and it designs, implements, debugs, and opens PRs — end to end. You only need to review.",
    purpose: "'I wish I could hand this feature off to someone' — now you can. Attracting attention as a replacement for outsourcing repetitive dev tasks.",
    features: ["Full autonomous development", "Design → implement → test", "Auto PR creation", "Browser & terminal control", "Slack integration"],
    pricing: "Core $20/mo / Team $500/mo",
    url: "https://devin.ai",
    icon: <Bot className="h-6 w-6" />,
  },
  {
    id: 20,
    name: "Replit Agent",
    category: "AICoding",
    description: "Complete apps built entirely in your browser. Describe your requirements in plain language and Replit handles environment setup, coding, and deployment automatically. Even beginners can build web apps.",
    purpose: "Zero setup for your dev environment. The shortest path from idea to working product. Also great for learning.",
    features: ["Browser-based", "Natural language → app", "Auto deployment", "DB/API integration", "Real-time collaboration"],
    pricing: "Free / Replit Core $25/mo",
    url: "https://replit.com",
    icon: <Code className="h-6 w-6" />,
  },
  {
    id: 106,
    name: "Gemini CLI",
    category: "AICoding",
    description: "Google's AI agent that brings Gemini directly to your terminal. Run dev tasks, manipulate files, search the web, and generate code right from the command line. Same concept as Claude Code, but powered by Gemini.",
    purpose: "For engineers who are comfortable with Gemini and want to boost productivity from the CLI. Use your AI assistant without ever leaving the terminal.",
    features: ["Terminal-native", "Gemini powered", "File automation", "Web search integration", "Code generation & editing"],
    pricing: "Free (Gemini API key required) / AI Pro $19.99/mo",
    url: "https://github.com/google-gemini/gemini-cli",
    icon: <Code className="h-6 w-6" />,
  },

  // ── Category 5: Build Agents ───────────────────────────────────
  {
    id: 21,
    name: "Bolt.new",
    category: "BuildAgents",
    description: "Build a full-stack app in seconds with natural language. An AI dev environment from StackBlitz. Generate front-end, back-end, and DB together in the browser, then deploy instantly.",
    purpose: "Just say 'I want a web app like this' and get a working prototype. For business people who want a landing page, internal tool, or MVP as fast as possible.",
    features: ["Full-stack auto-generation", "In-browser execution", "Instant deployment", "Multiple framework support", "Real-time preview"],
    pricing: "Free / Pro $20/mo / Team $40/mo",
    url: "https://bolt.new",
    icon: <Layout className="h-6 w-6" />,
  },
  {
    id: 22,
    name: "Lovable",
    category: "BuildAgents",
    description: "From idea to fully-fledged web app by talking. Built-in Supabase (DB) and Stripe (payments) integrations let you build production-ready apps with natural language alone.",
    purpose: "SaaS MVP development and early-stage startup products. Non-engineers can build apps with DB, auth, and payments all included — ready to go live.",
    features: ["Natural language → full-stack", "Supabase/Stripe integration", "GitHub sync", "Real-time editing", "Production deployment"],
    pricing: "Free / Starter $25/mo / Launch $50/mo / Scale $100/mo",
    url: "https://lovable.dev",
    icon: <Layout className="h-6 w-6" />,
  },
  {
    id: 23,
    name: "Vercel v0",
    category: "BuildAgents",
    description: "Generate professional UI components from text. Outputs copy-paste-ready code built with React/Next.js + shadcn/ui + Tailwind. Also imports Figma designs.",
    purpose: "Convert design comps to code or quickly prototype UIs. The bridge between designer and engineer — ideal for fast front-end iteration.",
    features: ["Auto UI generation", "React/Next.js ready", "shadcn/ui & Tailwind", "Figma import", "Code export"],
    pricing: "Free 200 credits/mo / Premium $20/mo",
    url: "https://v0.dev",
    icon: <Layout className="h-6 w-6" />,
  },

  // ── Category 6: Workflow Automation ───────────────────────────
  {
    id: 25,
    name: "Microsoft Copilot",
    category: "WorkflowAutomation",
    description: "AI that controls Word, Excel, PowerPoint, and Teams. Fully integrated into Microsoft 365 for data analysis, document creation, email summaries, and meeting notes — all by AI. Your most powerful daily business partner.",
    purpose: "Maximum impact for companies that live in Office. Excel analysis, PowerPoint generation, and Teams meeting summaries become dramatically easier.",
    features: ["Microsoft 365 integration", "Excel auto-analysis", "PowerPoint generation", "Teams meeting summary", "Outlook email summary"],
    pricing: "Copilot Pro ¥3,200/mo / M365 Copilot $30/seat/mo (M365 sold separately)",
    url: "https://copilot.microsoft.com",
    icon: <BarChart className="h-6 w-6" />,
  },
  {
    id: 26,
    name: "Zapier",
    category: "WorkflowAutomation",
    description: "The king of automation — connecting 7,000+ apps with AI. Automate repetitive tasks like Gmail → Slack notifications or form → spreadsheet logging with no code. AI decision nodes included.",
    purpose: "For everyone who does the same thing by hand every day. Across any department or role, automation saves dozens of hours a month.",
    features: ["7,000+ app integrations", "AI workflows", "No-code builder", "Conditional logic", "Scheduled runs"],
    pricing: "Free / Starter $29.99/mo / Professional $73.50/mo",
    url: "https://zapier.com",
    icon: <Bot className="h-6 w-6" />,
  },
  {
    id: 27,
    name: "n8n",
    category: "WorkflowAutomation",
    description: "Open-source automation you can self-host. Combine 400+ integrations with AI nodes to build complex workflows. Your data never leaves your infrastructure.",
    purpose: "For companies with strict security requirements or workflows too complex for Zapier. Full control on your own servers.",
    features: ["400+ integrations", "AI nodes", "Self-hostable", "Unlimited workflows", "Custom code"],
    pricing: "Self-host free / Cloud Starter €24/mo",
    url: "https://n8n.io",
    icon: <Bot className="h-6 w-6" />,
  },
  {
    id: 28,
    name: "Dify",
    category: "WorkflowAutomation",
    description: "Build internal AI chatbots with no code. Ingest company documents via RAG and create a custom AI assistant for your business. Design complex AI pipelines visually.",
    purpose: "When you want to build an FAQ bot or automate customer support. Open source and self-hostable, so sensitive data stays safe.",
    features: ["Visual workflow editor", "RAG engine", "MCP support", "50+ built-in tools", "Self-host / SaaS"],
    pricing: "Self-host free / Cloud Free tier available",
    url: "https://dify.ai",
    icon: <Bot className="h-6 w-6" />,
  },
  {
    id: 29,
    name: "Make",
    category: "WorkflowAutomation",
    description: "Build complex business flows visually. An intuitive visual editor lets you design branching, loops, and error handling for scenarios too complex for Zapier.",
    purpose: "For marketing automation, order → invoice → inventory workflows, and multi-step business process automation.",
    features: ["Visual editor", "Complex branching & loops", "1,800+ app integrations", "Data transformation", "Error handling"],
    pricing: "Free / Core $10.59/mo / Pro $18.82/mo",
    url: "https://make.com",
    icon: <Bot className="h-6 w-6" />,
  },

  // ── Category 7: Image & Design Generation ────────────────────
  {
    id: 30,
    name: "Midjourney",
    category: "ImageDesign",
    description: "The gold standard in AI image quality. v7 brings a huge leap in text rendering accuracy. Style references and draft mode let you express your brand's visual world consistently.",
    purpose: "Create advertising creatives, product concept visuals, and brand imagery. Also a powerful ideation tool for designers.",
    features: ["v7 high-quality generation", "Text rendering", "Style reference", "Fast draft mode", "Web app UI"],
    pricing: "Basic $10/mo / Standard $30/mo / Pro $60/mo",
    url: "https://midjourney.com",
    icon: <Palette className="h-6 w-6" />,
  },
  {
    id: 31,
    name: "DALL-E 3 / GPT Image",
    category: "ImageDesign",
    description: "Generate and edit images right inside a ChatGPT conversation. Accurately reflects natural language instructions, then refine or create variations on the spot. Deep ChatGPT integration is the key differentiator.",
    purpose: "Need an illustration for a presentation or a social media image? Just add 'make me an image too' while chatting in ChatGPT — effortless.",
    features: ["ChatGPT integration", "Image editing", "Text rendering", "Commercial use allowed", "Variation generation"],
    pricing: "ChatGPT Plus $20/mo / Pro $200/mo",
    url: "https://openai.com/dall-e-3",
    icon: <Palette className="h-6 w-6" />,
  },
  {
    id: 32,
    name: "Canva AI (Magic Studio)",
    category: "ImageDesign",
    description: "Anyone can create pro-quality design assets. AI image generation, background removal, video editing, and presentation creation — all in one platform. Unbeatable template library.",
    purpose: "The go-to design hub for teams without a dedicated designer. Social media graphics, flyers, business cards, video thumbnails — handles everything.",
    features: ["Magic Studio (AI integrated)", "Background removal", "Tens of thousands of templates", "Video editing", "Brand kit"],
    pricing: "Free / Pro ¥1,500/mo / Teams ¥1,800/mo",
    url: "https://canva.com",
    icon: <Palette className="h-6 w-6" />,
  },
  {
    id: 33,
    name: "Adobe Firefly",
    category: "ImageDesign",
    description: "AI image generation safe for commercial use. Trained on copyright-cleared data including Adobe Stock. Plugs directly into Photoshop and Illustrator to fit your existing workflow.",
    purpose: "For advertising, packaging, and web design where copyright risk must be avoided. Seamlessly adopted by existing Adobe users.",
    features: ["Commercially safe", "Creative Cloud integration", "Text effects", "Generative fill", "Style reference"],
    pricing: "Firefly Standard plan ¥680/mo / Included in Creative Cloud",
    url: "https://firefly.adobe.com",
    icon: <Palette className="h-6 w-6" />,
  },

  // ── Category 8: Video & Audio Generation ─────────────────────
  {
    id: 34,
    name: "Runway ML",
    category: "VideoAudio",
    description: "Generate and edit video from text or images. Gen-4 delivers major consistency improvements for characters and backgrounds. Act-One (motion transfer) and audio generation round out the full video production pipeline.",
    purpose: "Create promotional and social media videos without filming. An all-in-one tool for generating, editing, and adding effects to videos.",
    features: ["Gen-4 video generation", "Act-One (motion transfer)", "Audio generation", "Consistency preservation", "Video editing"],
    pricing: "Free / Standard $12/mo / Pro $28/mo",
    url: "https://runwayml.com",
    icon: <Video className="h-6 w-6" />,
  },
  {
    id: 35,
    name: "ElevenLabs",
    category: "VideoAudio",
    description: "Indistinguishable AI voices in 70+ languages. Type text and convert it to natural-sounding speech. Clone your own voice for multilingual narrations.",
    purpose: "Cut narration outsourcing costs, produce multilingual content in-house, and create audio guides. E-learning and internal training videos voiced in minutes.",
    features: ["High-quality voice synthesis", "70+ languages", "Voice cloning", "Real-time conversion", "API access"],
    pricing: "Free / Starter $5/mo / Creator $22/mo",
    url: "https://elevenlabs.io",
    icon: <Video className="h-6 w-6" />,
  },
  {
    id: 36,
    name: "HeyGen",
    category: "VideoAudio",
    description: "Generate videos where an AI avatar speaks for you. 100+ avatars with multilingual lip-sync let you create no-face-required promotional videos. Interactive avatars enable real-time conversations.",
    purpose: "Product demos, educational videos, and multilingual marketing. 'Film one video, auto-dub 30 language versions' — global rollout accelerated.",
    features: ["100+ AI avatars", "Multilingual lip-sync", "Interactive avatar", "Personal clone", "Rich templates"],
    pricing: "Free / Creator $24/mo / Business $39/seat/mo+",
    url: "https://heygen.com",
    icon: <Users className="h-6 w-6" />,
  },
  {
    id: 37,
    name: "Loom AI",
    category: "VideoAudio",
    description: "Screen recording plus AI for better async communication. AI automatically summarizes, transcribes, and extracts highlights from your recordings. Explain in 1 minute of video what would take 5 minutes in text.",
    purpose: "Remote work updates, product demos, and process walkthroughs. Cut meetings and share information asynchronously with video memos.",
    features: ["Screen recording", "AI auto-summary", "Transcription", "Highlight extraction", "Team sharing"],
    pricing: "Free / Business $15/seat/mo / Enterprise — contact for pricing",
    url: "https://loom.com",
    icon: <Video className="h-6 w-6" />,
  },

  // ── Category 9: Document & Business Efficiency ───────────────
  {
    id: 38,
    name: "Gamma",
    category: "DocEfficiency",
    description: "Paste text and a finished presentation appears. AI auto-generates the structure, design, and layout. Supports slides, documents, and web pages — share in one click.",
    purpose: "Cut presentation creation time by 90%. Solves the problem 'content is ready but design takes forever.'",
    features: ["AI auto-design", "Text → slides", "Web page output", "Team sharing", "Rich templates"],
    pricing: "Free / Plus $10/mo / Pro $20/mo",
    url: "https://gamma.app",
    icon: <FileText className="h-6 w-6" />,
  },
  {
    id: 39,
    name: "Notion AI",
    category: "DocEfficiency",
    description: "AI infused in your team's knowledge base. AI can search, summarize, and translate across all Notion documents. Auto-generate meeting notes and auto-organize tasks for the whole team.",
    purpose: "Essential for teams using Notion. 'Where was that document?' becomes an AI question. AI assists with meeting minutes, daily reports, and wiki pages.",
    features: ["Cross-document search", "Write & summarize", "Translation", "Q&A", "Task auto-organization"],
    pricing: "Free / Plus $12/mo / Business $18/seat/mo (AI included)",
    url: "https://notion.so",
    icon: <FileText className="h-6 w-6" />,
  },
  {
    id: 40,
    name: "Napkin AI",
    category: "DocEfficiency",
    description: "Paste text and watch it transform into diagrams and infographics. It analyzes text structure and auto-generates flowcharts, tables, and visuals. Instantly improves the visual appeal of blog posts and presentations.",
    purpose: "Turn text-only proposals into visually rich documents, add illustrations to blog posts — no design skills needed.",
    features: ["Text → diagram conversion", "Infographics", "Flowcharts", "Style customization", "Export"],
    pricing: "Free / Pro $15/mo",
    url: "https://napkin.ai",
    icon: <FileText className="h-6 w-6" />,
  },
  {
    id: 41,
    name: "DeepL",
    category: "DocEfficiency",
    description: "AI translation that captures nuance. Industry-leading quality for business documents. Write mode lets AI refine your text, and custom glossaries handle company-specific terminology.",
    purpose: "Translating international emails and contracts, creating multilingual documentation. Shows its true value in business settings where Google Translate falls short.",
    features: ["High-accuracy translation", "Write (text refinement)", "Custom glossary", "File translation", "30+ languages"],
    pricing: "Free / Starter $8.74/mo / Advanced $28.74/mo / Ultimate $57.49/mo",
    url: "https://deepl.com",
    icon: <Globe className="h-6 w-6" />,
  },
  {
    id: 42,
    name: "Otter.ai",
    category: "DocEfficiency",
    description: "Just invite it to a meeting and get instant minutes. Joins Zoom, Teams, and Google Meet automatically to transcribe in real time, identify speakers, summarize, and extract action items.",
    purpose: "For every business person who finds taking notes a waste of time. Summary and to-dos shared immediately after the meeting — focus on the next step.",
    features: ["Auto transcription", "Speaker identification", "AI summary", "Action item extraction", "Zoom/Teams/Meet integration"],
    pricing: "Free / Pro $8.33–16.99/mo / Business $20–30/mo",
    url: "https://otter.ai",
    icon: <FileText className="h-6 w-6" />,
  },

  // ── Category 10: Development Platform & Infrastructure ────────
  {
    id: 45,
    name: "Supabase",
    category: "BackendInfra",
    description: "The open-source Firebase alternative. A PostgreSQL-based BaaS providing database, auth, storage, Edge Functions, and real-time — all integrated. Row-Level Security and direct SQL access keep you free from vendor lock-in.",
    purpose: "Dramatically reduces the effort of building a backend from scratch. Manage auth, DB, and file storage from one dashboard — small teams can ship serious apps fast.",
    features: ["PostgreSQL DB", "Auth / RLS", "Storage", "Edge Functions", "Real-time", "Vector search support"],
    pricing: "Free $0 / Pro $25/mo / Team $599/mo / Enterprise custom",
    url: "https://supabase.com",
    icon: <Database className="h-6 w-6" />,
  },
  {
    id: 46,
    name: "Cloudflare Workers",
    category: "BackendInfra",
    description: "Serverless infrastructure running on 300+ global edge locations. Workers (backend), Pages (frontend), D1 (SQLite DB), and R2 (S3-compatible storage with free egress) — all integrated. Zero egress costs are the killer advantage.",
    purpose: "For companies wanting fast global APIs and websites at low cost. R2's free egress can slash costs dramatically compared to AWS S3.",
    features: ["Edge execution (300 locations)", "D1 (SQLite DB)", "R2 (free egress)", "Workers AI", "Pages (auto-deploy)", "Durable Objects"],
    pricing: "Free 100K requests/day / Paid $5/mo+ / R2 free egress",
    url: "https://developers.cloudflare.com",
    icon: <Cloud className="h-6 w-6" />,
  },
  {
    id: 47,
    name: "Vercel",
    category: "BackendInfra",
    description: "Deploy to the world with a single git push. The hosting platform from the creators of Next.js. Delivers auto-build → CDN deploy → preview URL workflows. v0 (AI UI generation) also integrated.",
    purpose: "Deploy Next.js/React/Vue frontend apps at maximum speed. Preview URLs simplify team reviews. v0 generates UI prototypes instantly.",
    features: ["Git-triggered auto-deploy", "Fluid Compute", "Preview URLs", "v0 (AI UI generation)", "ISR/SSR support", "Analytics"],
    pricing: "Hobby $0 (personal) / Pro $20/user/mo / Enterprise custom",
    url: "https://vercel.com",
    icon: <Globe className="h-6 w-6" />,
  },
  {
    id: 50,
    name: "GitHub",
    category: "BackendInfra",
    description: "The world's largest code sharing and CI/CD platform. The center of version control (Git), with GitHub Actions (CI/CD automation), GitHub Copilot (AI coding), Codespaces (cloud IDE), and Advanced Security (vulnerability detection) all integrated. 100M+ developers worldwide.",
    purpose: "The starting point for code management, team collaboration, automated testing, and deployment. In AI-driven development too, it's essential as the final code home and automation trigger.",
    features: ["Git version control", "GitHub Actions (CI/CD)", "GitHub Copilot integration", "Codespaces (cloud IDE)", "Advanced Security", "Pull Request / Code Review"],
    pricing: "Free / Pro $4/mo / Team $4/user/mo / Enterprise custom",
    url: "https://github.com",
    icon: <Code className="h-6 w-6" />,
  },

  // ── Category 11: Japan-Born AI ────────────────────────────────
  {
    id: 201,
    name: "Sakana AI",
    category: "JapanAI",
    description: "Japan's AI unicorn — Sakana Fugu released. A Tokyo-based AI startup (co-founded by Llion Jones and David Ha, ex-Google Brain). Officially launched 'Sakana Fugu' in June 2026, a multi-model integrator that surpasses Claude Fable and GPT-5.5 on coding benchmarks.",
    purpose: "For companies and researchers seeking a domestic, Japanese-native AI development partner. Valued at over ¥400B, it matches overseas rivals on technical merit.",
    features: ["Sakana Fugu (multi-model integration)", "AI Scientist (autonomous research)", "Evolutionary algorithms", "Defense & bio track record", "TinySwallow Japanese LLM"],
    pricing: "Research API: partially available / Sakana Fugu API: contact for pricing",
    url: "https://sakana.ai",
    icon: <Fish className="h-6 w-6" />,
  },
  {
    id: 202,
    name: "PLaMo 3.0 Prime (Preferred Networks)",
    category: "JapanAI",
    description: "Japan's full-scratch LLM — adopted by Japan's Digital Agency as Gennai. Developed by Preferred Networks (Tokyo). Released PLaMo 3.0 Prime in June 2026. The only domestic full-scratch model adopted for the Digital Agency's Gennai AI platform. Supports 256K context.",
    purpose: "Specialized for Japanese business documents and government use. Supports on-premise deployment where data stays domestic — ideal for healthcare, finance, and legal sectors.",
    features: ["Japanese full-scratch LLM", "256K token support", "Digital Agency Gennai adoption", "Reasoning mode", "Low price (¥60/MTok)"],
    pricing: "Standard: input ¥60 / output ¥250 per MTok / Enterprise — contact for pricing",
    url: "https://plamo.preferredai.jp",
    icon: <Brain className="h-6 w-6" />,
  },
  {
    id: 203,
    name: "rinna",
    category: "JapanAI",
    description: "Japanese-specialized conversational and character AI. Developed by rinna Co., Ltd. (spun off from Microsoft Japan's Japanese AI division). Ranges from LINE/SNS character AI to business-grade Japanese LLMs.",
    purpose: "For use cases requiring native Japanese conversation — customer support bots, SNS characters, internal inquiry systems. Open-source versions available for easy evaluation.",
    features: ["Japanese conversation specialization", "Character AI", "LINE/SNS support", "OSS version available", "API access"],
    pricing: "OSS version free / Enterprise API — contact for pricing",
    url: "https://rinna.co.jp",
    icon: <Bot className="h-6 w-6" />,
  },

  // ── Category 12: Chinese AI ───────────────────────────────────
  {
    id: 301,
    name: "DeepSeek V4 / R1",
    category: "ChinaAI",
    isChina: true,
    description: "Open-source LLM with outstanding cost efficiency. Developed by Chinese company DeepSeek. Released 1.6T-parameter V4-Pro in April 2026. SWE-bench 80.6% at 1/6 the cost of Claude Opus. R1 is a reasoning-specialized model. Shook the AI industry in early 2025.",
    purpose: "For cost-conscious AI developers and researchers. Self-host the open-weight version to keep sensitive data off the cloud.",
    features: ["GPT-5.4-level performance", "Low-cost inference (1/6 of Opus)", "R1 (advanced reasoning)", "Open weights available", "V4-Pro (1.6T parameters)"],
    pricing: "API V4: low cost / OSS version for self-hosting",
    url: "https://deepseek.com",
    icon: <Brain className="h-6 w-6" />,
  },
  {
    id: 302,
    name: "Kimi K2 (Moonshot AI)",
    category: "ChinaAI",
    isChina: true,
    description: "Coding-specialized LLM with 1T parameters. Developed by Chinese company Moonshot AI. Released 'Kimi K2.7-Code' in June 2026. 262K context, SWE-bench 84%+ with top-tier coding agent performance. Demonstrated autonomous 300-agent runs over 12+ hours.",
    purpose: "Best for large-scale coding and long-document analysis. API pricing at ~1/18 of competitors. Never input sensitive data — self-hosting on domestic servers recommended.",
    features: ["262K token support", "Coding agent specialization", "300 parallel agents", "Multimodal support", "Available on Hugging Face"],
    pricing: "API: input $0.95 / output $4.00 per MTok / Hugging Face OSS version available",
    url: "https://kimi.ai",
    icon: <FileText className="h-6 w-6" />,
  },
  {
    id: 303,
    name: "Qwen (Tongyi Qianwen)",
    category: "ChinaAI",
    isChina: true,
    description: "Alibaba's multilingual multimodal LLM. The Qwen series from Alibaba Group supports multiple languages and handles text, image, and audio multimodal processing. Open-weight Qwen2.5-Max is widely used.",
    purpose: "For processing Chinese business documents or tasks requiring Asian multilingual support. Self-hosting open-weight versions is possible, but check the license and terms carefully.",
    features: ["Multilingual support", "Multimodal", "Open weights available", "Qwen3-Coder (coding specialized)", "API access"],
    pricing: "API pay-as-you-go / Open-weight version for self-hosting",
    url: "https://qwenlm.github.io",
    icon: <Globe className="h-6 w-6" />,
  },
  {
    id: 304,
    name: "Manus",
    category: "ChinaAI",
    isChina: true,
    description: "The fully autonomous AI agent. Developed by Chinese company Monica.im. Stunned the AI industry after a March 2025 demo video. Fully automates web app building and deployment from text instructions alone. Multi-agent architecture auto-selects between Claude, GPT, and Gemini.",
    purpose: "For automating complex multi-step workflows end-to-end. Executes web research, spreadsheet creation, and code implementation in sequence. Now generally available as of 2026.",
    features: ["Fully autonomous task execution", "Multi-agent architecture", "Browser automation", "Desktop app support", "Japanese support"],
    pricing: "Free (1–3 tasks/day) / Plus $39/mo",
    url: "https://manus.im/ja",
    icon: <Bot className="h-6 w-6" />,
  },
];

// ---------------------------------------------------------------------------
// ページコンポーネント
// ---------------------------------------------------------------------------
export default function AIToolsPage() {
  const { t, i18n } = useTranslation();
  const isJa = i18n.language === 'ja' || i18n.language.startsWith('ja');
  const categories = isJa ? categoriesJa : categoriesEn;
  const aiTools = isJa ? aiToolsJa : aiToolsEn;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
      <div className="max-w-6xl mx-auto px-4">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.back_home')}
          </Button>
        </Link>

        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">{t('ai_tools.page_title')}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('ai_tools.page_subtitle')}
          </p>
        </div>

        {categories.map((cat) => {
          const tools = aiTools.filter((tool) => tool.category === cat.key);
          if (tools.length === 0) return null;
          const isChineseCategory = cat.key === "中国系AI" || cat.key === "ChinaAI";
          return (
            <div key={cat.key} className="mb-16">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{cat.label}</h2>
                <p className="text-gray-500">{cat.subtitle}</p>
                {isChineseCategory && (
                  <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700 font-medium">
                      {t('ai_tools.china_warning_category')}
                    </p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {tools.map((tool) => (
                  <Card key={tool.id} className={`hover:shadow-xl transition-shadow duration-300 ${tool.isChina ? "border-red-200" : ""}`}>
                    {tool.isChina && (
                      <div className="flex items-start gap-2 bg-red-50 border-b border-red-200 px-4 py-2 rounded-t-lg">
                        <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-red-700 font-medium">
                          {t('ai_tools.china_warning_card')}
                        </p>
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${tool.isChina ? "bg-red-100" : "bg-blue-100"}`}>
                          {tool.icon}
                        </div>
                        <div>
                          <CardTitle className="text-xl">{tool.name}</CardTitle>
                          <Badge variant={tool.isChina ? "destructive" : "secondary"}>{tool.category}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <CardDescription className="text-gray-600 text-sm leading-relaxed">
                        {tool.description}
                      </CardDescription>

                      {tool.purpose && (
                        <div className={`rounded-lg p-3 ${tool.isChina ? "bg-red-50" : "bg-blue-50"}`}>
                          <h4 className={`font-semibold text-sm mb-1 ${tool.isChina ? "text-red-900" : "text-blue-900"}`}>{t('ai_tools.use_case_label')}</h4>
                          <p className={`text-sm ${tool.isChina ? "text-red-800" : "text-blue-800"}`}>{tool.purpose}</p>
                        </div>
                      )}

                      <div>
                        <h4 className="font-semibold mb-2 text-sm">{t('ai_tools.features_label')}</h4>
                        <div className="flex flex-wrap gap-2">
                          {tool.features.map((feature, index) => (
                            <Badge key={index} variant="outline" className="text-xs">{feature}</Badge>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t">
                        <div>
                          <p className="text-sm font-medium">{t('ai_tools.pricing_label')}</p>
                          <p className="text-sm text-gray-600">{tool.pricing}</p>
                        </div>
                        <Button asChild variant={tool.isChina ? "outline" : "default"}>
                          <a href={tool.url} target="_blank" rel="noopener noreferrer">
                            {t('topic_item.view_detail')}
                            <ExternalLink className="ml-2 h-4 w-4" />
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}

        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">{t('ai_tools.cta_title')}</h2>
          <p className="text-gray-600 mb-6">
            {t('ai_tools.cta_body')}
          </p>
          <Button size="lg" asChild>
            <Link to="/#contact">
              {t('ai_tools.cta_button')}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
